import queryString from 'query-string';

import { TTileLayerOptions } from '@/types/wmts-tile-layer';
import { TGetTileMatrixParamArgs } from './types';

const getTileMatrixParam = ({
  matrixIds,
  coords,
  zoomOffset
}: TGetTileMatrixParamArgs): string => {
  const matrixIdSample = matrixIds[0].identifier[0];

  const zoomValue =
    coords?.z && typeof zoomOffset === 'number' ? coords.z + zoomOffset : '{z}';

  // EPSG:0000:0
  const colonPattern = /:\d+$/;
  if (colonPattern.test(matrixIdSample)) {
    return matrixIdSample.replace(colonPattern, `:${zoomValue}`);
  }

  return '{z}';
};

// The TileMatrix parameter in a WMTS request is not always a simple number
// representing the zoom level. Sometimes, it takes different formats, e.g.,
// Geoportal uses a notation like: EPSG:2180:1 for zoom 1, EPSG:2180:2 for zoom 2, etc.
// The method below returns the correct TileMatrix with the numeric value replaced
// by Leaflet's {z}, which Leaflet will substitute with the appropriate zoom level.
const getWmtsTileLayerUrl = ({
  url,
  layer,
  style,
  format,
  tileMatrixSet,
  matrixIds,
  zoomOffset,
  coords
}: TTileLayerOptions): string => {
  const query = {
    SERVICE: 'WMTS',
    VERSION: '1.0.0',
    REQUEST: 'GetTile',
    LAYER: layer,
    STYLE: style,
    FORMAT: format,
    TILEMATRIXSET: tileMatrixSet,
    // In some cases, replacing the values derived from coords by Leaflet,
    // i.e., x, y, and z in the URL, is impossible (e.g., when generating the
    // appropriate URL in an extended TileLayer class).
    // Therefore, I pass coords to the function responsible for generating
    // the URL and do it manually.
    TILEMATRIX: getTileMatrixParam({ matrixIds, coords, zoomOffset }),
    TILEROW: coords?.y ?? '{y}',
    TILECOL: coords?.x ?? '{x}'
  };

  return queryString.stringifyUrl(
    {
      url,
      query
    },
    { encode: false }
  );
};

export default getWmtsTileLayerUrl;
